import React, { useMemo, useState } from "react";
import { RefreshCw, Search } from "lucide-react";
import {
  Button,
  DatePickerDateRange,
  Input,
  SearchDropdownSingleSelect,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "lift-ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ClaudeCodeProvider, useClaudeCode } from "../context/ClaudeCodeContext";
import StatsCards from "./StatsCards";
import UserAnalytics from "./user-analytics/UserAnalytics";
import AppAnalytics from "./app-analytics/AppAnalytics";
import KeyUsage from "./key-usage/KeyUsage";
import { displayNameFromEmail } from "./UserCell";
import type { AppStatus } from "../utils/types";
import "../claude-code-dashboard.styles.scss";

type DashboardTab = "user" | "app" | "key";

const toIsoDate = (d: Date | undefined): string | undefined => {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fromIsoDate = (s: string | undefined): Date | undefined => {
  if (!s) return undefined;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

const DashboardInner: React.FC = () => {
  const [activeTab, setActiveTab] = useState<DashboardTab>("user");
  const {
    sharedFilters,
    setSharedFilters,
    sessionFilters,
    setSessionFilters,
    appFilters,
    setAppFilters,
    keyUsageFilters,
    setKeyUsageFilters,
    users,
    refreshAll,
    statsLoading,
    appsLoading,
    sessionsLoading,
    keyUsageLoading,
  } = useClaudeCode();

  const isRefreshing =
    statsLoading || appsLoading || sessionsLoading || keyUsageLoading;

  const userItems = useMemo(
    () => [
      { id: "all", label: "All Users" },
      ...users.map((u) => ({
        id: u.user_email,
        label: u.user_name?.trim() || displayNameFromEmail(u.user_email),
      })),
    ],
    [users]
  );

  const dateRangePicker = (
    <div className="min-w-[260px]">
      <DatePickerDateRange
        initialStartDate={fromIsoDate(sharedFilters.start_date)}
        initialEndDate={fromIsoDate(sharedFilters.end_date)}
        onApply={(start, end) =>
          setSharedFilters({
            ...sharedFilters,
            start_date: toIsoDate(start),
            end_date: toIsoDate(end),
          })
        }
        fiscal={false}
        yearType="calendar"
      />
    </div>
  );

  const renderHeaderControls = () => {
    if (activeTab === "user") {
      return (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative w-[200px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search sessions..."
              value={sessionFilters.search}
              onChange={(e) =>
                setSessionFilters({
                  ...sessionFilters,
                  search: e.target.value,
                  page: 0,
                })
              }
              className="h-8 w-full pl-8"
            />
          </div>
          <SearchDropdownSingleSelect
            items={userItems}
            selectedItem={sessionFilters.user_email}
            onSelectionChange={(v) =>
              setSessionFilters({ ...sessionFilters, user_email: v, page: 0 })
            }
            placeholder="Search users..."
            triggerLabel={
              userItems.find((i) => i.id === sessionFilters.user_email)
                ?.label ?? "All Users"
            }
            className="w-[220px] h-8"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => refreshAll()}
            disabled={isRefreshing}
            className="gap-1 px-3 h-8 text-sm"
          >
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          {dateRangePicker}
        </div>
      );
    }
    if (activeTab === "key") {
      return (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative w-[220px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search keys / teams..."
              value={keyUsageFilters.search}
              onChange={(e) =>
                setKeyUsageFilters({
                  ...keyUsageFilters,
                  search: e.target.value,
                  page: 0,
                })
              }
              className="h-8 w-full pl-8"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refreshAll()}
            disabled={isRefreshing}
            className="gap-1 px-3 h-8 text-sm"
          >
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          {dateRangePicker}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-[200px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search apps..."
            value={appFilters.search}
            onChange={(e) =>
              setAppFilters({ ...appFilters, search: e.target.value, page: 0 })
            }
            className="h-8 w-full pl-8"
          />
        </div>
        <Select
          value={appFilters.status}
          onValueChange={(v) =>
            setAppFilters({
              ...appFilters,
              status: v as AppStatus | "all",
              page: 0,
            })
          }
        >
          <SelectTrigger className="w-[140px] h-8">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="deploying">Deploying</SelectItem>
            <SelectItem value="deployed">Deployed</SelectItem>
            <SelectItem value="live">Live</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <SearchDropdownSingleSelect
          items={userItems}
          selectedItem={appFilters.user_email}
          onSelectionChange={(v) =>
            setAppFilters({ ...appFilters, user_email: v, page: 0 })
          }
          placeholder="Search users..."
          triggerLabel={
            userItems.find((i) => i.id === appFilters.user_email)?.label ??
            "All Users"
          }
          className="w-[220px] h-8"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => refreshAll()}
          disabled={isRefreshing}
          className="gap-1 px-3 h-8 text-sm"
        >
          <RefreshCw
            className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
        {dateRangePicker}
      </div>
    );
  };

  return (
    <div className="claude-code-dashboard flex flex-col h-full bg-white">
      <header className="relative w-full flex flex-col items-start p-0 box-border bg-gradient-to-b from-white from-[calc(100%-1px)] to-[#E5E1DF] to-[calc(100%-1px)] pb-0">
        <div className="flex justify-between items-center w-full p-4 gap-3 pb-2">
          <div className="flex items-center gap-3 flex-1 min-w-0 flex-wrap">
            <h2 className="font-semibold text-[1.125rem] leading-[24px] tracking-[-0.2px] text-[#242424] break-words">
              Claude Code Dashboard
            </h2>
          </div>
          <div className="flex flex-row items-center gap-2 flex-wrap flex-shrink-0 ml-auto">
            {renderHeaderControls()}
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-hidden flex flex-col px-8 py-4 bg-white">
        <div className="mb-4 flex-shrink-0">
          <StatsCards />
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as DashboardTab)}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="mb-3 flex-shrink-0">
            <TabsList className="inline-flex w-fit">
              <TabsTrigger value="user">User Analytics</TabsTrigger>
              <TabsTrigger value="app">App Analytics</TabsTrigger>
              <TabsTrigger value="key">Key Usage</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent
            value="user"
            className="flex-1 overflow-hidden flex flex-col mt-0"
          >
            <UserAnalytics />
          </TabsContent>

          <TabsContent
            value="app"
            className="flex-1 overflow-hidden flex flex-col mt-0"
          >
            <AppAnalytics />
          </TabsContent>

          <TabsContent
            value="key"
            className="flex-1 overflow-hidden flex flex-col mt-0"
          >
            <KeyUsage />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const ClaudeCodeDashboard: React.FC = () => (
  <ClaudeCodeProvider>
    <DashboardInner />
  </ClaudeCodeProvider>
);

export default ClaudeCodeDashboard;
